import { useMemo } from "react";
import {
  CancellationPolicyEnum,
  LodgingMediaAssetEnum,
  Payment,
  type FiatPrice,
  type HotelItinerary,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { htmlDecode } from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import { formatGuestSummary } from "../../utils/formatGuests";
import { getPayAtPropertyBreakdown } from "./getPayAtPropertyBreakdown";

export const useCheckoutBreakdown = (itinerary: HotelItinerary) => {
  const { t, formatFiatCurrency, formatRewardsCurrency } = useI18nContext();

  return useMemo(() => {
    const res = itinerary.reservation;
    const nightCount = dayjs(res.checkOutDate).diff(
      dayjs(res.checkInDate),
      "day"
    );
    const roomInfo = res.bookedRooms?.[0]?.roomInfo ?? res.roomInfo;
    const roomCount = res.bookedRooms?.[0]?.count ?? 1;
    const rawRoomName = roomInfo?.name ?? "";
    const roomName = htmlDecode(rawRoomName) ?? rawRoomName;
    const roomLabel = roomCount > 1 ? `${roomCount}x ${roomName}` : roomName;

    const image =
      roomInfo?.media
        ?.filter((m) => m.type !== LodgingMediaAssetEnum.Video)
        ?.at(0)?.url ??
      res.lodgingData?.media
        ?.filter((m) => m.type !== LodgingMediaAssetEnum.Video)
        ?.at(0)?.url;

    const adultCount = res.adultCount ?? 0;
    const childCount = res.childCount ?? 0;
    const childrenAges = res.childrenAges ?? [];
    const guestSummary = formatGuestSummary(
      t,
      adultCount,
      childCount,
      childrenAges
    );

    const cancellationPolicy = res.cancellationPolicy;
    const isRefundable =
      cancellationPolicy?.CancellationPolicy ===
      CancellationPolicyEnum.Refundable;

    const subtotal = formatFiatCurrency(res.pricing.subtotal.fiat);
    const taxes = formatFiatCurrency(res.pricing.taxes.fiat);

    const formatFee = (fiat?: FiatPrice) =>
      fiat != null && fiat.value > 0 ? formatFiatCurrency(fiat) : undefined;

    // pricingV2-first (FTC/EAN-compliant); falls back to legacy fee/tax
    // breakdown for historical reservations without pricingV2.
    const payAtProperty = getPayAtPropertyBreakdown(res);
    const resortFees = formatFee(payAtProperty.resortFees);
    const mandatoryFee = formatFee(payAtProperty.mandatoryFee);
    const mandatoryTax = formatFee(payAtProperty.mandatoryTax);
    const feeTotal = formatFee(payAtProperty.feeTotal);

    const hasPayAtProperty =
      resortFees != null ||
      mandatoryFee != null ||
      mandatoryTax != null ||
      feeTotal != null;
    const total = formatFiatCurrency(itinerary.productTotal.fiat);

    const paymentLineItems = itinerary.paymentBreakdown?.paymentLineItems;
    const rewardsPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.RbcAvionPoints
    );
    const cardPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.Card
    );
    const offerPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.TravelWalletOffer
    );
    const creditPayment = paymentLineItems?.find(
      ({ type }) => type === Payment.TravelWalletCredit
    );

    const numberOfFlexiblePoints =
      rewardsPayment?.value?.pointsRedemptionBreakdown
        ?.numberOfFlexiblePoints ?? 0;

    const pointsRedeemed =
      numberOfFlexiblePoints > 0
        ? formatRewardsCurrency(
            {
              value: numberOfFlexiblePoints,
              currency: "Points",
            },
            { minimumFractionDigits: 0, maximumFractionDigits: 0 }
          )
        : undefined;

    const flexFiat =
      rewardsPayment?.value?.pointsRedemptionBreakdown
        ?.flexiblePointsCashEquivalent;
    const pointsRedeemedFiat = flexFiat?.value
      ? `-${formatFiatCurrency({
          value: flexFiat.value,
          currencyCode: flexFiat.currencyCode ?? "CAD",
        })}`
      : "";

    const travelCreditAmount = creditPayment?.value?.paymentAmount?.amount ?? 0;
    const travelCreditApplied =
      creditPayment && travelCreditAmount > 0
        ? `-${formatFiatCurrency({
            value: travelCreditAmount,
            currencyCode:
              creditPayment.value?.paymentAmount?.currency ??
              itinerary.productTotal.fiat.currencyCode,
          })}`
        : undefined;

    const amountPaid = formatFiatCurrency({
      value: cardPayment?.value?.paymentAmount?.amount ?? 0,
      currencyCode:
        cardPayment?.value?.paymentAmount?.currency ??
        itinerary.productTotal.fiat.currencyCode,
    });

    const cardNumber = cardPayment?.value?.cardNumberDisplay;
    const cardLabel = t("core-hotels.paymentBreakdown.label.payments.card");
    const paymentMethod =
      cardNumber != null ? `${cardLabel} (${cardNumber})` : cardLabel;

    const offerFiatAmount = offerPayment?.value?.paymentAmount?.amount ?? 0;
    const offerAmount =
      offerFiatAmount > 0
        ? `-${formatFiatCurrency({
            value: offerFiatAmount,
            currencyCode:
              offerPayment?.value?.paymentAmount?.currency ??
              itinerary.productTotal.fiat.currencyCode,
          })}`
        : undefined;

    const cfarFiat = itinerary.cfar?.price?.fiat;
    const cfarPrice = cfarFiat ? formatFiatCurrency(cfarFiat) : undefined;
    return {
      roomLabel,
      guestSummary,
      image,
      isRefundable,
      cancellationPrimaryText: cancellationPolicy?.primaryText ?? "",
      nightCount,
      roomCount,
      subtotal,
      taxes,
      resortFees,
      mandatoryFee,
      mandatoryTax,
      feeTotal,
      hasPayAtProperty,
      total,
      hasCfar: itinerary.cfar != null,
      cfarPrice,
      offerAmount,
      travelCreditApplied,
      amountPaid,
      pointsRedeemed,
      pointsRedeemedFiat,
      paymentMethod,
    };
  }, [itinerary, formatFiatCurrency, formatRewardsCurrency, t]);
};
